import React, { Component } from 'react';

import {
    onlyLetterValdation,
    emailValidation,
    setTrackingEvent,
    doubleInt,
    isOfAge,
    postcodeValidation,
    setProfileGender,
} from 'utils';
import FormButton from 'components/FormButton/FormButton';
import InputField from 'components/InputField/InputField';
import FormError from 'components/FormError/FormError';
import FormDropdown from 'components/FormDropdown/FormDropdown';
import RegisterRestriction from 'components/RegisterRestriction/RegisterRestriction';
import DataDisclosure from 'components/DataDisclosure/DataDisclosure';
import MarketingConsent from 'containers/MarketingConsent/MarketingConsent';
import {
    shouldShowMarketingConsentModal,
    MarketingConsentModalConfig,
} from 'containers/MarketingConsent/MarketingConsent.helper';
import UserService from 'utils/userService';
import { setCookie, hasCookie } from 'utils/cookie';
import {
    USER_ERROR_EXISTS,
    Screens,
    INVALID_EMAIL_ERROR,
    GENDER_OPTIONS,
} from 'containers/constants';

import './Register.scss';

const U18_COOKIE = 'My5_U18_Lockout';
const U18_LOCKOUT_MINUTES = 60;
const U18_POLL_INTERVAL_MS = 10_000;

interface IRegisterProps {
    handleScreen: Function;
    handleUserUpdate: Function;
    marketingConsentConfig?: MarketingConsentModalConfig;
}

export default class Register extends Component<IRegisterProps, any> {
    private lockoutInterval: ReturnType<typeof setInterval> | null = null;

    state = {
        isLoading: false,
        registered: false,
        showMarketingConsent: false,
        given_name: '',
        family_name: '',
        dob_day: '',
        dob_month: '',
        dob_year: '',
        postcode: '',
        gender: '',
        email: '',
        password: '',
        calendarDate: new Date(),
        errors: {
            postcode: '',
            email: '',
            dob: '',
            password: '',
            given_name: '',
            family_name: '',
            gender: '',
        },
        responseError: '',
        isValidatingEmail: false,
        needsEmailRevalidation: false,
        isRestricted: hasCookie(U18_COOKIE),
    };

    componentDidMount(): void {
        setTrackingEvent('regStart', 'registration', { registrationStep: 'started' });
        if (this.state.isRestricted) {
            this.startLockoutPolling();
        }
    }

    componentWillUnmount(): void {
        if (!this.state.registered) setTrackingEvent('regAbandon', 'registration', { registrationStep: 'abandoned' });
        this.stopLockoutPolling();
    }

    startLockoutPolling = (): void => {
        this.lockoutInterval = setInterval(() => {
            if (!hasCookie(U18_COOKIE)) {
                this.setState({ isRestricted: false });
                this.stopLockoutPolling();
            }
        }, U18_POLL_INTERVAL_MS);
    };

    stopLockoutPolling = (): void => {
        if (this.lockoutInterval) {
            clearInterval(this.lockoutInterval);
            this.lockoutInterval = null;
        }
    };

    handleSignin = () => {
        setTrackingEvent('regAbandon', 'registration', { registrationStep: 'abandoned' });
        this.props.handleScreen(Screens.SIGNIN);
    };

    prefixZero = (e: React.ChangeEvent<HTMLInputElement>): React.ChangeEvent<HTMLInputElement> => {
        const value = e.target.value;
        e.target.value = value.length === 1 && value !== '0' ? `0${value}` : value;
        e.target.value = e.target.value.length > 2 ? parseInt(e.target.value).toString() : e.target.value;
        return e;
    };

    validateFormFields = async (event: any) => {
        event.preventDefault && event.preventDefault();
        const { name, value } = event.target;
        let errors = this.state.errors;
        switch (name) {
            case 'given_name':
                if (!value || value.trim() === '') {
                    errors.given_name = 'First name is required';
                } else if (!onlyLetterValdation(value)) {
                    errors.given_name = 'Must be letters only';
                } else if (value.length > 50) {
                    errors.given_name = 'Can not be longer than 50 charactes';
                } else {
                    errors.given_name = '';
                }
                break;
            case 'family_name':
                if (!value || value.trim() === '') {
                    errors.family_name = 'Last name is required';
                } else if (!onlyLetterValdation(value)) {
                    errors.family_name = 'Must be letters only';
                } else if (value.length > 50) {
                    errors.family_name = 'Can not be longer than 50 charactes';
                } else {
                    errors.family_name = '';
                }
                break;
            case 'email':
                errors.email = value && !emailValidation(value) ? 'Email is not valid' : '';

                if (!errors.email && value) {
                    this.setState({ isValidatingEmail: true, errors, [name]: value });
                    try {
                        const userService = new UserService();
                        const result = await userService.emailValidation(value);
                        if (this.state.email === value) {
                            const emailParts = Object.values(result);
                            errors.email = !emailParts.every((v) => v) ? 'Please enter a valid email' : '';
                            this.setState({ errors, isValidatingEmail: false, needsEmailRevalidation: false });
                        } else {
                            this.setState({ isValidatingEmail: false });
                        }
                    } catch (error) {
                        this.setState({
                            isValidatingEmail: false,
                            ...(this.state.email === value ? { needsEmailRevalidation: false } : {}),
                        });
                    }
                    return;
                }
                break;
            case 'password':
                if (value && value.length < 6) {
                    errors.password = 'Password must be 6 characters long';
                } else if (value && !/^[\S]+.*[\S]+$/.test(value)) {
                    errors.password = 'Password must not start or end with a space';
                } else {
                    errors.password = '';
                }
                break;
            case 'postcode':
                errors.postcode = value && !postcodeValidation(value) ? 'Please enter a valid postcode' : '';
                errors.postcode =
                    value.toUpperCase() !== value ? 'Please enter postcode all in CAPITALS' : errors.postcode;
                break;
            case 'dob_day':
            case 'dob_month':
            case 'dob_year':
                const { dob_day, dob_month, dob_year } = this.state;
                // check date
                const dateString = `${dob_month}/${dob_day}/${dob_year}`;
                const selectedDob = new Date(dateString);
                if (dob_day === '' || dob_month === '' || dob_year === '' || dob_year.length < 4) {
                    errors.dob = 'This is a required field';
                } else if (selectedDob.toString() === 'Invalid Date' || parseInt(dob_year) < 1900) {
                    errors.dob = 'Please enter a valid date of birth';
                } else if (parseInt(dob_month) < selectedDob.getMonth() + 1) {
                    // check for intercalary year
                    errors.dob = 'Please enter a valid date of birth';
                } else {
                    errors.dob = '';
                }
                break;
            case 'gender':
                errors.gender = value ? '' : 'This is a required field';
                break;
            default:
                break;
        }
        this.setState({ errors, [name]: value });
    };

    validateForm = () => {
        const fieldsAreValid = () => {
            let valid = true;
            Object.values(this.state.errors).forEach(
                // if we have an error string set valid to false
                (val) => val.length > 0 && (valid = false)
            );
            return valid;
        };
        return (
            !this.state.isLoading &&
            !this.state.isValidatingEmail &&
            !this.state.needsEmailRevalidation &&
            this.state.email.length > 0 &&
            this.state.dob_day.length > 0 &&
            this.state.dob_month.length > 0 &&
            this.state.dob_year.length > 0 &&
            this.state.password.length > 0 &&
            this.state.given_name?.trim().length > 0 &&
            this.state.family_name?.trim().length > 0 &&
            this.state.gender.length > 0 &&
            fieldsAreValid()
        );
    };

    handlechange = (event: any, callback?: Function) => {
        this.setState(
            {
                [event.target.id]: event.target.value,
                ...(event.target.id === 'email' ? { needsEmailRevalidation: true, isValidatingEmail: false } : {}),
            },
            () => callback && callback(event)
        );
    };

    validateAllFields = async (): Promise<boolean> => {
        const fieldNames = ['given_name', 'family_name', 'password', 'postcode', 'dob_day', 'email', 'gender'] as const;
        for (const name of fieldNames) {
            await this.validateFormFields({ target: { name, value: this.state[name] }, preventDefault: () => {} });
        }
        return Object.values(this.state.errors).every((val) => val === '');
    };

    handleSubmit = async (event: any) => {
        event.preventDefault();
        if (this.state.isLoading) return;

        this.setState({ isLoading: true, responseError: '' });

        const isValid = await this.validateAllFields();
        if (!isValid) {
            this.setState({ isLoading: false });
            return;
        }

        const { given_name, family_name, email, password, postcode, gender, dob_day, dob_month, dob_year } = this.state;

        const dobISO = `${dob_year}-${doubleInt(dob_month)}-${doubleInt(dob_day)}`;
        if (!isOfAge(dobISO, 18)) {
            setCookie(U18_COOKIE, dobISO, { expiresInMinutes: U18_LOCKOUT_MINUTES });
            this.setState({ isRestricted: true, isLoading: false });
            this.startLockoutPolling();
            return;
        }

        setTrackingEvent('regSubmit', 'registration', { registrationStep: 'submitted' });

        if (!email) {
            setTrackingEvent('regError', 'registration', { errorCode: `Invalid email address` });
            this.setState({ responseError: INVALID_EMAIL_ERROR, isLoading: false });
            return;
        }

        const dob = `${doubleInt(dob_day)}/${doubleInt(dob_month)}/${dob_year}`;
        try {
            const userService = new UserService();
            const result = await userService.signUp(
                email.toLowerCase().trim(),
                password,
                given_name.trim(),
                family_name.trim(),
                dob,
                postcode.trim(),
                gender
            );

            await this.props.handleUserUpdate();

            if (result && result.userConfirmed) {
                await userService.signIn(email.toLowerCase(), password);
                const userInfo = await userService.getUserInfo();
                const shouldShowMarketingConsent =
                    !!this.props.marketingConsentConfig && (await shouldShowMarketingConsentModal());

                await this.props.handleUserUpdate();
                this.setState({ registered: true, isLoading: false }, () => {
                    setProfileGender(userInfo.gender);
                    setTrackingEvent('regComplete', 'registration', {
                        registrationStep: 'completed',
                        profileID: userInfo.userId,
                        regUsrAgeBracket: userInfo.ageBracket,
                        loggedInStatus: 'logged in',
                        ageBracket: userInfo.ageBracket,
                        ...(userInfo.gender && { gender: userInfo.gender }),
                    });

                    if (shouldShowMarketingConsent) {
                        this.setState({ showMarketingConsent: true });
                    } else {
                        // newAccount drives Pin's "your account has been created" copy.
                        this.props.handleScreen(Screens.PIN_SCREEN, true);
                    }
                });
            } else {
                setTrackingEvent('regError', 'registration', {
                    errorCode: `The response of Cognito SignUp is null`,
                });
                const responseError = result.code === 'UsernameExistsException' ? USER_ERROR_EXISTS : result.message;
                this.setState({ responseError, isLoading: false }, () => {
                    if (result.message?.toLowerCase().includes("'password'")) {
                        document.getElementById('password')?.focus();
                    }
                });
            }
        } catch (e) {
            const { code, message } = e as { code: string; message: string };
            const responseError = code === 'UsernameExistsException' ? USER_ERROR_EXISTS : message;
            setTrackingEvent('regError', 'registration', { errorCode: `${code} (${code})` });
            this.setState({ responseError, isLoading: false });
        }
    };

    completeMarketingConsent = () => {
        this.setState({ showMarketingConsent: false }, () => this.props.handleScreen(Screens.PIN_SCREEN, true));
    };

    render() {
        const {
            given_name,
            family_name,
            email,
            dob_day,
            dob_month,
            dob_year,
            password,
            postcode,
            gender,
            errors,
            responseError,
            isLoading,
            showMarketingConsent,
            isRestricted,
        } = this.state;

        if (isRestricted) {
            return <RegisterRestriction onSignIn={this.handleSignin} />;
        }

        return (
            <>
                <form onSubmit={this.handleSubmit} className="register">
                    <DataDisclosure />
                    <div className="register-fields">
                        <div className="column-2">
                            <InputField
                                inputtype="text"
                                inputvalue={given_name}
                                labeltext="First name *"
                                inputid="given_name"
                                handlechange={this.handlechange}
                                blurValidation={this.validateFormFields}
                                error={errors.given_name && errors.given_name}
                            />
                            <InputField
                                inputtype="text"
                                inputvalue={family_name}
                                labeltext="Last name *"
                                inputid="family_name"
                                handlechange={this.handlechange}
                                blurValidation={this.validateFormFields}
                                error={errors.family_name && errors.family_name}
                            />
                        </div>
                        <InputField
                            inputtype="text"
                            inputvalue={postcode.toUpperCase()}
                            labeltext="Postcode"
                            inputid="postcode"
                            handlechange={this.handlechange}
                            blurValidation={this.validateFormFields}
                            error={errors.postcode && errors.postcode}
                        />
                        <div className="form-input-group">
                            <div className="form-input-group double-label">
                                <label className="input-label">Date of Birth *</label>
                            </div>
                            <div className="row date">
                                <InputField
                                    inputtype="number"
                                    inputvalue={dob_day}
                                    labeltext="Day"
                                    placeholder="DD"
                                    min={1}
                                    max={31}
                                    inputid="dob_day"
                                    handlechange={(e: any) =>
                                        this.handlechange(this.prefixZero(e), this.validateFormFields)
                                    }
                                    blurValidation={this.validateFormFields}
                                    classNames="no-padding"
                                />
                                <InputField
                                    inputtype="number"
                                    inputvalue={dob_month}
                                    labeltext="Month"
                                    placeholder="MM"
                                    min={1}
                                    max={12}
                                    inputid="dob_month"
                                    handlechange={(e: any) =>
                                        this.handlechange(this.prefixZero(e), this.validateFormFields)
                                    }
                                    blurValidation={this.validateFormFields}
                                    classNames="no-padding"
                                />
                                <InputField
                                    inputtype="number"
                                    inputvalue={dob_year}
                                    labeltext="Year"
                                    placeholder="YYYY"
                                    min={1900}
                                    inputid="dob_year"
                                    handlechange={(e: any) => this.handlechange(e, this.validateFormFields)}
                                    blurValidation={this.validateFormFields}
                                    classNames="no-padding"
                                />
                            </div>
                            {errors.dob && <FormError error={errors.dob} />}
                        </div>
                        <div className="form-input-group">
                            <label className="input-label" htmlFor="gender">
                                Gender *
                            </label>
                            <FormDropdown
                                dropdownId="gender"
                                dropdownName="gender"
                                dropdownValue={gender}
                                options={GENDER_OPTIONS}
                                placeholder="Select"
                                classNames={`gender-select ${gender ? '' : 'placeholder'}`}
                                handlechange={(e: any) => this.handlechange(e, this.validateFormFields)}
                                blurValidation={this.validateFormFields}
                            />
                            {errors.gender && <FormError error={errors.gender} />}
                        </div>
                        <InputField
                            inputtype="email"
                            inputvalue={email}
                            labeltext="Email address *"
                            inputid="email"
                            handlechange={this.handlechange}
                            blurValidation={this.validateFormFields}
                            error={errors.email && errors.email}
                        />
                        <InputField
                            inputtype="password"
                            inputvalue={password}
                            labeltext="Password *"
                            inputid="password"
                            handlechange={(e: any) =>
                                this.handlechange(e, errors.password ? this.validateFormFields : undefined)
                            }
                            showHide
                            blurValidation={this.validateFormFields}
                            error={errors.password && errors.password}
                        />
                    </div>
                    <div className="submit-container">
                        {isLoading && (
                            <div className="spinner-container">
                                <div className="spinner"></div>
                            </div>
                        )}
                        <FormButton
                            disabled={!this.validateForm()}
                            buttonType="submit"
                            buttonText="Create Account"
                            buttonId="submit-form"
                            dataTestId="submit-form-btn"
                        />
                        {responseError && <FormError error={responseError} />}
                    </div>
                    <div className="extra-content terms">
                        <span className="terms-confirm-text light">
                            By continuing, you confirm that you have read our
                        </span>
                        <a
                            className="link"
                            href="https://privacy.paramount.com/policy"
                            rel="noopener noreferrer nofollow"
                            target="_blank"
                        >
                            Privacy Policy,
                        </a>
                        <span className="terms-confirm-text light">and that you accept our</span>
                        <a
                            className="link"
                            href="http://www.channel5.com/terms-and-conditions"
                            rel="noopener noreferrer nofollow"
                            target="_blank"
                        >
                            Terms & Conditions.
                        </a>
                    </div>
                    <div className="extra-content req-acc">
                        <span className="terms-confirm-text">* Required field to set up your account</span>
                    </div>
                    <div className="extra-content">
                        <span className="create-account">Already Have An Account?</span>
                        <button id="login-link" className="link" onClick={this.handleSignin}>
                            Sign In Here
                        </button>
                    </div>
                </form>
                <MarketingConsent
                    open={showMarketingConsent}
                    onDone={this.completeMarketingConsent}
                    config={this.props.marketingConsentConfig}
                />
            </>
        );
    }
}
